export type ServiceTicketVariant = 'CUSTOM' | 'BREAKFIX' | 'INSTALLATION' | 'SITE_SURVEY' | 'VIRTUAL_SITE_SURVEY';

export type ServiceTicketStatus =
  | 'requested'
  | 'reschedule_needed'
  | 'scheduled'
  | 'in_progress'
  | 'revisit_needed'
  | 'completed'
  | 'cancelled'
  | 'on_hold'
  | 'site_readiness_scheduled'
  | 'site_readiness_completed'
  | 'site_survey_required'
  | 'site_survey_completed';

export type CustomServiceType = {
  serviceTicketId?: number;
  description: string;
};

export type BreakfixServiceType = {
  serviceTicketId?: number;
  description: string;
};

export type SiteSurveyServiceType = {
  serviceTicketId?: number;
  description: string;
};

export type VirtualSiteSurveyServiceType = {
  serviceTicketId?: number;
  description: string;
};

export type InstallationServiceType = {
  isNewLine: boolean;
  isCutoverInsideWiringNeeded: boolean;
  isSiteUnderConstruction: boolean;
};

export type RequestedDateTimeType = {
  date: string;
  timeOptionId: number;
};

export type ServiceOrganizationSystemType = {
  id: number;
  name: string;
};

export type ServiceOrganizationListItemType = {
  id: number;
  name: string;
  system?: ServiceOrganizationSystemType | null;
};

export type TechnicianType = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: {
    number: string;
    countryCode: string;
  };
  serviceZipCodes?: string[];
  serviceOrganizations?: Array<{ id: number; name: string }>;
  isRehirable?: boolean;
  notes?: string;
};

export type CCUserType = {
  id?: number;
  userId?: number;
  firstName: string;
  lastName: string;
  email: string;
};

export type AssignedUserType = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
};

export type ChecklistItemType = 'CHECKBOX' | 'YES_NO';

export type ChecklistStatusType = 'CHECKLIST_IN_PROGRESS' | 'CHECKLIST_COMPLETED';

export type ChecklistAutomationRuleType = 'ATTACHMENTS_UPLOADED' | 'FORM_UPLOADED' | 'SCHEDULED_DATETIME_SET';

export type ChecklistTemplateItemType = {
  id: number;
  label: string;
  itemType: ChecklistItemType;
  isRequired: boolean;
  sortOrder: number;
  automationRule?: ChecklistAutomationRuleType;
};

export type ChecklistTemplateType = {
  id: number;
  name: string;
  serviceVariant: ServiceTicketVariant | number;
  isDefault: boolean;
  isActive: boolean;
  items: ChecklistTemplateItemType[];
};

export type ServiceTicketChecklistItemType = {
  id: number;
  templateItemId: number;
  label: string;
  itemType: ChecklistItemType;
  isRequired: boolean;
  sortOrder: number;
  checked: boolean;
  value?: string;
  checkedByUserId?: number;
  checkedAt?: string;
  isAutoChecked: boolean;
  autoCheckSource?: string;
  notes?: string;
  automationRule?: ChecklistAutomationRuleType;
};

export type ServiceTicketChecklistType = {
  id: number;
  serviceTicketId: number;
  templateId: number;
  status: ChecklistStatusType;
  completedByUserId?: number;
  completedAt?: string;
  items: ServiceTicketChecklistItemType[];
  template?: ChecklistTemplateType;
  progress?: {
    completedItems: number;
    totalItems: number;
    completedRequiredItems: number;
    totalRequiredItems: number;
    status: ChecklistStatusType;
  };
};

export type AtRiskReason =
  | 'NONE'
  | 'INSTALLATION_NO_TRACKING_WITHIN_4_DAYS'
  | 'INSTALLATION_DELIVERY_ON_OR_AFTER_SCHEDULED'
  | 'SITE_SURVEY_NO_TECH_WITHIN_3_DAYS'
  | 'PAST_SLA';

export interface AtRiskInfo {
  isAtRisk: boolean;
  reason: AtRiskReason;
  reasonLabel: string;
}

export enum SiteReadinessEvaluationStatus {
  Ready = 'ready',
  NotReady = 'not_ready',
  NotStarted = 'not_started',
}

export enum SiteReadinessEvaluationReason {
  BuildingType = 'building_type',
  WallMountingMaterial = 'wall_mounting_material',
  PermanentPower = 'permanent_power',
  TelcoRoomPhotos = 'telco_room_photos',
  CellSignals = 'cell_signals',
  IssuesOrConcerns = 'issues_or_concerns',
  NearestPowerLocation = 'nearest_power_location',
  NoPower = 'no_power',
  PoorCellSignal = 'poor_cell_signal',
}

export type SiteReadinessEvaluation = {
  status: SiteReadinessEvaluationStatus;
  answeredCount: number;
  applicableCount: number;
  missingReasons: SiteReadinessEvaluationReason[];
  expiresAt?: string;
};

export interface ServiceTicketType {
  id: number;
  displayName?: string;
  hash?: string;
  createdAt: string;
  modifiedAt: string;
  lastUpdatedAt?: string;
  orgId?: number;
  locationId?: number;
  org?: { id: number; name: string };
  resellerOrganization?: { id: number; name: string; level?: number };
  location?: { id: number; name: string; timezone: string };
  requestedDateTimes: RequestedDateTimeType[];
  scheduledForDate?: string | null;
  scheduledForTimeOptionId?: number | null;
  status: ServiceTicketStatus;
  scheduledForDateTime?: string | null;
  dispatcherTechnicianId?: string | null;
  dispatcherServiceId?: string | null;
  serviceVariant: ServiceTicketVariant;
  isSiteUnderConstruction?: boolean;
  customService: CustomServiceType | null;
  breakfixService: BreakfixServiceType | null;
  siteSurveyService: SiteSurveyServiceType | null;
  virtualSiteSurveyService: VirtualSiteSurveyServiceType | null;
  installationService: InstallationServiceType | null;
  contacts?: Array<{
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: {
      number: string;
      countryCode: string;
    };
    role: string;
  }>;
  technician?: TechnicianType;
  technicians?: TechnicianType[];
  ccNotifyUsers?: CCUserType[];
  assignedUsers?: AssignedUserType[];
  associatedTelcobillServiceOrderId: number | null;
  associatedTelcobillInventoryOrderId: number | null;
  associatedOrderId: number | null;
  associatedInventoryOrders?: AssociatedInventoryOrderSummary[];
  associatedSupportTicketId: number | null;
  associatedSiteSurveyServiceCallId: number | null;
  associatedInstallationServiceCallId: number | null;
  atRisk?: AtRiskInfo;
  siteReadinessEvaluation?: SiteReadinessEvaluation;
  holdReason?: string | null;
  [key: string]: any;
}

export type AssociatedInventoryOrderPackageSummary = {
  packageName: string;
  trackingNumber: string | null;
  carrier: string | null;
  shippingDate: string | null;
  carrierStatus: string | null;
};

export type AssociatedInventoryOrderDeviceSummary = {
  deviceCategory: string;
  modelName: string | null;
};

export type AssociatedInventoryOrderSummary = {
  id: number;
  status: string;
  packages: AssociatedInventoryOrderPackageSummary[];
  devices: AssociatedInventoryOrderDeviceSummary[];
  shippingDate: string | null;
  expectedDeliveryDate: string | null;
};

export interface ContactFormData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  role: string;
}

export interface TechnicianFormData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: { number: string; countryCode: string };
  serviceOrganization?: { id: number; name: string };
}

export interface ContactFormType {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  role: string;
}

export interface ServiceCallRequestFormData {
  organization: OptionType | null;
  location: OptionType | null;
  requestType: OptionType | null;
  customDescription: string;
  isCutoverInsideWiringNeeded: OptionType | null;
  isSiteUnderConstruction: OptionType | null;
  newLinesOrExistingLines: OptionType | null;
  date1: string;
  timeSlot1: OptionType | null;
  date2: string;
  timeSlot2: OptionType | null;
  contacts: OptionType[];
  phone: string;
  phoneCountryCode: string;
  contactForms: ContactFormData[];
  selectedTechnician: TechnicianFormData | null;
  selectedServiceOrganization: { id: number; name: string } | null;
  ccUsers: AssignedUserType[];
  assignedUsers: AssignedUserType[];
  orderId?: number;
  ticketId?: number;
}

// Define types for options
export type OptionType = {
  id: number | string;
  name: string;
  contact?: {
    firstName: string;
    lastName: string;
    email: string;
    mobile?: string;
    phone?: string;
    mobileCountryCode?: string;
    role?: string;
  };
};

export interface CreatedBy {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
}

export interface Note {
  id: number;
  message: string;
  createdBy: CreatedBy;
  createdAt: string;
  modifiedAt: string;
}

export interface User {
  userId: number;
  firstName: string;
  lastName: string;
  email?: string;
}

export interface ActionLog {
  id: number;
  action: string;
  previousValue?: string;
  newValue?: string;
  user?: User;
  createdAt: string;
  modifiedAt: string;
}

export type ServiceTicketsStats = {
  ticketsByStatus?: {
    REQUESTED?: number;
    SCHEDULED?: number;
    IN_PROGRESS?: number;
    ON_HOLD?: number;
    COMPLETED?: number;
    CANCELLED?: number;
  };
};
